import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { Card } from '../../ui/Card'
import { useConfirmare } from '../../ui/Confirmare'
import { BON_LABEL, BonuriEsuate, EMAIL_LABEL, ModalBon } from '../predariComune'
import { faraAcces, MESAJ_FARA_SAMPLING } from './comun'
import { deschidePdf } from './deschidePdf'

/**
 * Ce s-a dat efectiv pe teren, la evenimentul ăsta.
 *
 * Predările există de mult, dar se vedeau doar din modulul de sampling, filtrate pe acțiune.
 * Întrebarea „ce s-a dat la Sense4Fit" cerea să știi dinainte ce acțiuni au fost — aici răspunsul
 * vine de la sine, peste toate acțiunile activității.
 */

interface Predare {
  id: number
  nrPv: string | null
  createdAt: string
  statusEmail: string | null
  statusBon: string | null
  emailEroare?: string | null
  contact: {
    nume: string | null
    prenume: string | null
    email: string | null
    telefon: string | null
  } | null
  actiune: { id: number; denumire: string } | null
  /** Numărul bonului de consum din EA, când a fost emis. Blochează ștergerea. */
  bonEaDocNo?: string | null
  bonStornatLa?: string | null
  anulataLa?: string | null
  /** Ce a scris omul în formular, la „Observații (opțional)”. */
  observatii?: string | null
  linii: { denumire: string; cantitate: number; um: string }[]
}

function clipa(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

type Filtru = 'toate' | 'bon_esuat' | 'email_esuat' | 'anulate'

export function FilaPredari({ campanieId }: { campanieId: number }) {
  const [cauta, setCauta] = useState('')
  const [filtru, setFiltru] = useState<Filtru>('toate')
  const [bonPredareId, setBonPredareId] = useState<number | null>(null)
  const qc = useQueryClient()
  const { confirma, cere } = useConfirmare()
  /*
    Anularea, bonul si „bon stornat" cer dreptul de scriere pe sampling — aceeasi regula ca pe
    server si ca in Sampling → Predari. Pana pe 15.10.2026 le aveai doar acolo; „Bon stornat" era
    aici doar al adminilor, desi serverul il lasa oricui poate scrie.
  */
  const poateScrie = useAuthStore((s) => s.permissions).includes('marketing.sampling:write')
  /*
    Stergerea e a adminilor, si e altceva decat anularea.

    Anularea lasa PV-ul in evidenta, ca document justificativ — asa se scoate o predare reala
    facuta gresit. Stergerea e pentru ce n-ar fi trebuit sa existe: un PV de test. Alexandru,
    22.09.2026, dupa ce a generat PV-2026-000020 ca proba.
  */
  const eAdmin = useAuthStore((s) => s.user?.role) === 'admin'
  const q = useQuery<Predare[]>({
    queryKey: ['sampling', 'predari', campanieId],
    queryFn: async () =>
      (
        await api.get<Predare[]>('/marketing/sampling/predari', {
          params: { campanieId, limit: 500 },
        })
      ).data,
  })
  const toate = q.data ?? []
  const potriveste: Record<Filtru, (p: Predare) => boolean> = {
    toate: () => true,
    bon_esuat: (p) => p.statusBon === 'esuat' && !p.anulataLa,
    email_esuat: (p) => p.statusEmail === 'esuat' && !p.anulataLa,
    anulate: (p) => !!p.anulataLa,
  }
  const predari = toate
    .filter(potriveste[filtru])
    .filter(
      (p) =>
        !cauta.trim() ||
        `${p.contact?.nume ?? ''} ${p.contact?.prenume ?? ''} ${p.nrPv ?? ''}`
          .toLowerCase()
          .includes(cauta.trim().toLowerCase()),
    )
  // Actiunile cu bonuri respinse — retrimiterea in masa se face pe actiune, ca in Sampling.
  const actiuniCuEsuate = [
    ...new Map(
      toate
        .filter((p) => p.statusBon === 'esuat' && !p.anulataLa && p.actiune)
        .map((p) => [p.actiune!.id, p.actiune!] as const),
    ).values(),
  ]

  const bucati = toate.reduce((s, p) => s + p.linii.reduce((x, l) => x + l.cantitate, 0), 0)

  /*
    Declararea stornării, ca să se poată șterge o predare cu bon emis.

    Butonul exista doar în modulul de sampling, pe altă pagină. Mesajul de eroare de la ștergere
    trimitea la el — „apasă «Bon stornat»" — iar aici nu era nicăieri (Alexandru, 22.09.2026:
    „unde apăs Bon stornat?"). Un mesaj care arată spre un buton de pe alt ecran nu e un drum.
  */
  const bonStornat = useMutation({
    mutationFn: async (id: number) => api.post(`/marketing/sampling/predari/${id}/bon-stornat`, {}),
    onSuccess: () => {
      toast.success('Notat. Acum predarea se poate șterge.')
      void qc.invalidateQueries({ queryKey: ['sampling', 'predari', campanieId] })
    },
    onError: (e) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Nu am putut nota stornarea',
      ),
  })

  /*
    Anularea: PV-ul ramane document justificativ, doar iese din evidenta activa, cu motivul scris.
    Era doar in Sampling → Predari; „folositi anularea din modulul de sampling" spunea textul de
    la stergere, adica alt ecran.
  */
  const anuleaza = useMutation({
    mutationFn: async (v: { id: number; motiv: string }) =>
      api.post(`/marketing/sampling/predari/${v.id}/anuleaza`, { motiv: v.motiv }),
    onSuccess: () => {
      toast.success('Predare anulată')
      void qc.invalidateQueries({ queryKey: ['sampling', 'predari', campanieId] })
      void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
    },
    onError: (e) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Predarea nu s-a putut anula',
      ),
  })

  async function cereAnularea(p: Predare) {
    const motiv = await cere({
      titlu: `Anulezi ${p.nrPv ?? 'predarea'}?`,
      mesaj: 'PV-ul rămâne document justificativ; predarea iese din evidența activă.',
      eticheta: 'De ce (rămâne scris în evidență)',
      buton: 'Anulează predarea',
      obligatoriu: true,
    })
    if (motiv === null) return
    if (motiv.trim().length < 3) {
      toast.error('Scrie un motiv, măcar trei litere.')
      return
    }
    anuleaza.mutate({ id: p.id, motiv: motiv.trim() })
  }

  const sterge = useMutation({
    mutationFn: async (id: number) =>
      (await api.delete<{ nrPv: string | null }>(`/marketing/sampling/predari/${id}`)).data,
    onSuccess: (r) => {
      toast.success(`Predarea ${r.nrPv ?? ''} a fost ștearsă`.trim())
      void qc.invalidateQueries({ queryKey: ['sampling', 'predari', campanieId] })
      void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
    },
    onError: (e) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Predarea nu s-a putut șterge',
      ),
  })

  if (faraAcces(q.error))
    return (
      <Card title="Predări & PV">
        <p className="text-sm text-b4l-text-muted">{MESAJ_FARA_SAMPLING}</p>
      </Card>
    )
  return (
    <Card title={`Predări & PV${toate.length ? ` · ${toate.length}` : ''}`}>
      {toate.length === 0 ? (
        <p className="text-sm text-b4l-text-muted">
          Nicio predare. Aici apar oamenii care au primit mostre la acțiunile activității, pe măsură
          ce semnează.
        </p>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-3">
            <input
              value={cauta}
              onChange={(e) => setCauta(e.target.value)}
              placeholder="Caută după nume sau număr de PV…"
              className="w-72 rounded border border-b4l-border px-2 py-1.5 text-sm"
            />
            <span className="text-xs text-b4l-text-muted">
              {toate.length} predări · {bucati} bucăți date
            </span>
          </div>
          <div role="group" aria-label="Filtru predări" className="mb-3 flex flex-wrap gap-1.5">
            {(
              [
                ['toate', 'Toate'],
                ['bon_esuat', 'Bon respins'],
                ['email_esuat', 'Email eșuat'],
                ['anulate', 'Anulate'],
              ] as const
            ).map(([k, t]) => {
              const n = toate.filter(potriveste[k]).length
              if (k !== 'toate' && n === 0) return null
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={filtru === k}
                  onClick={() => setFiltru(k)}
                  className={`rounded-full border px-3 py-1 text-[12.5px] ${
                    filtru === k
                      ? 'border-b4l-text bg-b4l-text text-b4l-surface'
                      : 'border-b4l-border bg-b4l-surface hover:bg-b4l-bg-soft'
                  }`}
                >
                  {t} <span className="tabular-nums opacity-70">{n}</span>
                </button>
              )
            })}
          </div>
          {actiuniCuEsuate.map((a) => (
            <div key={a.id} className="mb-3">
              <p className="mb-1 text-xs text-b4l-text-muted">{a.denumire}</p>
              <BonuriEsuate actiuneId={a.id} esuateInLista={0} canWrite={poateScrie} />
            </div>
          ))}
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-b4l-text-dim">
              <tr>
                <th className="py-2">PV</th>
                <th>Când</th>
                <th>Cui</th>
                <th>Acțiunea</th>
                <th>Ce s-a dat</th>
                <th>Email</th>
                <th>Bon EA</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {predari.map((p) => (
                <tr key={p.id} className="border-t border-b4l-border align-top">
                  <td className="py-2 font-mono text-xs">
                    {p.nrPv ?? '—'}
                    {p.anulataLa && <span className="ml-1 text-b4l-red-dark">(anulat)</span>}
                  </td>
                  <td className="text-xs text-b4l-text-muted">{clipa(p.createdAt)}</td>
                  <td>
                    {[p.contact?.nume, p.contact?.prenume].filter(Boolean).join(' ') || '—'}
                    <div className="text-xs text-b4l-text-dim">
                      {[p.contact?.email, p.contact?.telefon].filter(Boolean).join(' · ')}
                    </div>
                    {/* „Observatii (optional)" din formularul de la QR (22.10.2026). Doua randuri, restul la hover. */}
                    {p.observatii && (
                      <div
                        className="mt-0.5 line-clamp-2 max-w-xs whitespace-pre-line break-words text-xs text-b4l-text-muted"
                        title={p.observatii}
                      >
                        <span className="font-medium">Obs.:</span> {p.observatii}
                      </div>
                    )}
                  </td>
                  <td className="text-xs">{p.actiune?.denumire ?? '—'}</td>
                  <td className="text-xs">
                    {p.linii.map((l) => `${l.denumire} × ${l.cantitate}`).join(', ')}
                  </td>
                  <td className="text-xs">
                    <span
                      className={
                        p.statusEmail === 'esuat' ? 'text-b4l-red-dark' : 'text-b4l-text-muted'
                      }
                      title={p.emailEroare ?? undefined}
                    >
                      {p.statusEmail ? (EMAIL_LABEL[p.statusEmail] ?? p.statusEmail) : '—'}
                    </span>
                  </td>
                  <td className="text-xs">
                    <span
                      className={
                        p.statusBon === 'esuat' ? 'text-b4l-red-dark' : 'text-b4l-text-muted'
                      }
                    >
                      {p.statusBon ? (BON_LABEL[p.statusBon] ?? p.statusBon) : '—'}
                      {p.bonEaDocNo ? ` · ${p.bonEaDocNo}` : ''}
                    </span>
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {/* Documentul exista doar daca s-a dat marfa — ca in Sampling → Predari. */}
                    {p.linii.length > 0 && (
                      <>
                        <button
                          onClick={() =>
                            void deschidePdf(
                              `/marketing/sampling/predari/${p.id}/pdf`,
                              `${p.nrPv ?? 'PV'}.pdf`,
                            )
                          }
                          className="text-xs text-b4l-blue-dark hover:underline"
                        >
                          PV
                        </button>
                        <button
                          onClick={() => setBonPredareId(p.id)}
                          className="ml-3 text-xs text-b4l-text-muted hover:underline"
                        >
                          Bon
                        </button>
                      </>
                    )}
                    {poateScrie && p.bonEaDocNo && !p.bonStornatLa && !p.anulataLa && (
                      <button
                        onClick={async () => {
                          if (
                            await confirma({
                              titlu: `Bonul ${p.bonEaDocNo} a fost stornat în ExpertAccounts?`,
                              mesaj:
                                'CRM-ul nu poate verifica asta. Rămâne scris că ai declarat-o tu.',
                              buton: 'Da, e stornat',
                            })
                          )
                            bonStornat.mutate(p.id)
                        }}
                        disabled={bonStornat.isPending}
                        className="ml-3 text-xs text-b4l-amber hover:underline disabled:opacity-40"
                        title={`Bon ${p.bonEaDocNo} — deblochează ștergerea`}
                      >
                        Bon stornat
                      </button>
                    )}
                    {/*
                      Cu bon emis si nestornat, serverul refuza anularea (marfa a iesit din
                      gestiune). Atunci pasul de facut e „Bon stornat" — nu un buton care da eroare.
                    */}
                    {poateScrie && !p.anulataLa && !(p.bonEaDocNo && !p.bonStornatLa) && (
                      <button
                        onClick={() => void cereAnularea(p)}
                        disabled={anuleaza.isPending}
                        className="ml-3 text-xs text-b4l-red-dark hover:underline disabled:opacity-40"
                      >
                        Anulează
                      </button>
                    )}
                    {eAdmin && (
                      <button
                        onClick={async () => {
                          const nume =
                            [p.contact?.nume, p.contact?.prenume].filter(Boolean).join(' ') ||
                            'contactul'
                          if (
                            await confirma({
                              titlu: `Ștergi definitiv ${p.nrPv ?? 'predarea'} (${nume})?`,
                              mesaj:
                                'Nu se mai poate reface. Pentru o predare reală făcută greșit, ' +
                                'folosește „Anulează” — PV-ul rămâne document justificativ.',
                              buton: 'Șterge definitiv',
                              pericol: true,
                            })
                          )
                            sterge.mutate(p.id)
                        }}
                        disabled={sterge.isPending}
                        className="ml-3 text-xs text-b4l-red hover:underline disabled:opacity-40"
                      >
                        Șterge
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {predari.length === 0 && (
            <p className="py-4 text-center text-sm text-b4l-text-muted">
              Nicio predare pentru filtrul sau căutarea aleasă.
            </p>
          )}
        </>
      )}
      {bonPredareId != null && (
        <ModalBon
          predareId={bonPredareId}
          canWrite={poateScrie}
          onClose={() => {
            setBonPredareId(null)
            void qc.invalidateQueries({ queryKey: ['sampling', 'predari', campanieId] })
          }}
        />
      )}
    </Card>
  )
}
